import React, { ComponentProps, forwardRef, memo } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { Backspace } from '@surglogs-app/icons'
import { absoluteVerticalCenter, Box, Icon } from '@surglogs-app/ui'

import { DateInput } from './DateInput'

interface ClearableDateInputProps extends ComponentProps<typeof DateInput> {
  onClearDateClick?: () => void
  inline?: boolean
}

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => !['fullWidth'].includes(prop),
})<{ fullWidth: boolean }>`
  position: relative;
  width: 100%;
  max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '150px')};
`

const ClearDateIcon = styled(Icon)`
  ${absoluteVerticalCenter()};
  right: ${({ theme }) => theme.ui.spacings.md};
  cursor: pointer;
`

const StyledDateInput = styled(DateInput)<{
  hasClearButton?: boolean
  fullWidth: boolean
}>`
  max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '150px')};

  ${({ hasClearButton }) =>
    hasClearButton
      ? css`
          padding-right: 40px;
        `
      : ''}
`

export const ClearableDateInput = memo(
  forwardRef<HTMLInputElement, ClearableDateInputProps>(
    (
      {
        value,
        readOnly,
        disabled,
        onClearDateClick,
        fullWidth = false,
        inline = false,
        ...rest
      },
      ref,
    ) => {
      const hasClearButton = Boolean(
        onClearDateClick && !disabled && !readOnly && value,
      )
      const inlineProps = {
        size: 'mini',
        bw: 'abs.0',
        bc: 'blue.100',
        bb: 'solid 1px',
        bra: 'abs.0',
        bgc: 'transparent',
      } as const

      return (
        <StyledContainer {...{ fullWidth }}>
          <StyledDateInput
            {...rest}
            {...{
              disabled,
              ref,
              readOnly,
              value,
              hasClearButton,
              clearable: true,
              fullWidth,
              ...(inline ? inlineProps : {}),
            }}
          />
          {hasClearButton ? (
            <ClearDateIcon
              {...{
                icon: Backspace,
                onClick: onClearDateClick,
                variant: 'primary',
                'aria-label': 'Clear date',
                role: 'button',
              }}
            />
          ) : null}
        </StyledContainer>
      )
    },
  ),
)
